import { create } from 'zustand';
import axios from 'axios';
import { http } from '../api/http.js';

export interface SafeUser {
  id: string;
  orgId: string;
  email: string;
  name: string;
  role: string;
  v1Role?: string | null;
  /** Platform role reported by the server at sign-in. UX routing only: the
   *  server re-derives it from the database on every platform request. */
  platformRole?: 'SUPER_ADMIN' | null;
  createdAt: string;
  lastLoginAt: string | null;
}

interface Org {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

interface AuthState {
  token: string | null;
  user: SafeUser | null;
  org: Org | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, orgName?: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const authApi = axios.create({ baseURL: '/api', timeout: 10000 });

function loadInitialState(): { token: string | null; user: SafeUser | null; org: Org | null; isAuthenticated: boolean } {
  try {
    const token = localStorage.getItem('auth_token');
    if (token === null) return { token: null, user: null, org: null, isAuthenticated: false };
    const userStr = localStorage.getItem('auth_user');
    const orgStr = localStorage.getItem('auth_org');
    if (userStr === null || orgStr === null) {
      localStorage.removeItem('auth_token');
      return { token: null, user: null, org: null, isAuthenticated: false };
    }
    const user = JSON.parse(userStr) as SafeUser;
    const org = JSON.parse(orgStr) as Org;
    return { token, user, org, isAuthenticated: true };
  } catch {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_org');
    return { token: null, user: null, org: null, isAuthenticated: false };
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  ...loadInitialState(),
  isLoading: false,
  error: null,

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await authApi.post<{ success: boolean; token: string; user: SafeUser; org: Org }>(
        '/auth/login', { email, password },
      );
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      localStorage.setItem('auth_org', JSON.stringify(data.org));
      set({ token: data.token, user: data.user, org: data.org, isAuthenticated: true, isLoading: false });
    } catch (err: unknown) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.error?.message ?? err.response?.data?.message ?? err.message
        : 'Login failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  register: async (email, password, name, orgName) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await authApi.post<{ success: boolean; token: string; user: SafeUser; org: Org }>(
        '/auth/register', { email, password, name, orgName },
      );
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      localStorage.setItem('auth_org', JSON.stringify(data.org));
      set({ token: data.token, user: data.user, org: data.org, isAuthenticated: true, isLoading: false });
    } catch (err: unknown) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.error?.message ?? err.response?.data?.message ?? err.message
        : 'Registration failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_org');
    set({ token: null, user: null, org: null, isAuthenticated: false, error: null });
  },

  clearError: () => set({ error: null }),
}));

// Verify a restored localStorage session against the backend on startup. A
// token that no longer maps to a real, active user (e.g. after a factory
// reset) must not let the SPA boot into a logged-in view. The shared `http`
// client's 401 interceptor clears the auth keys and returns to /login.
export async function validateStoredSession(): Promise<void> {
  if (import.meta.env.MODE === 'test') return;
  const { token } = useAuthStore.getState();
  if (token === null) return;
  try {
    const { data } = await http.get<{ success: boolean; user: SafeUser | null; org: Org | null }>('/auth/me');
    if (data.success === true && data.user !== null && data.user !== undefined && data.org !== null && data.org !== undefined) {
      localStorage.setItem('auth_user', JSON.stringify(data.user));
      localStorage.setItem('auth_org', JSON.stringify(data.org));
      useAuthStore.setState({ user: data.user, org: data.org, isAuthenticated: true });
    } else {
      useAuthStore.getState().logout();
    }
  } catch {
    // The http 401 interceptor already clears auth_* and redirects to /login.
  }
}

if (typeof window !== 'undefined' && useAuthStore.getState().token !== null) {
  void validateStoredSession();
}
